<template>
  <div v-rect-loading="loading" class="edit-company-container">
    <div :class="['company-info-container', { 'wide-mode': isEn() }]">
      <WrappedFrom
        ref="form"
        class="company-info"
        :model="companyInfo"
        :formLines="formLines"
      >
        <template #logo>
          <div class="upload-setting">
            <div class="image-selector">
              <el-button class="select-file" @click="handleSelectLogoImage">{{ $t(`${i18nPrefix}.btn.upload`) }}</el-button>
              <input ref="logoFileSelector" class="file-selector" type="file" :accept="acceptImageFileTypeList.join(',')" @change="handleLogoImageChange"/>
              <div class="logo-tip">{{ $t(`${i18nPrefix}.label.logoTip`, { width: 300, height: 300 }) }}</div>
            </div>
            <div class="logo-container">
              <div class="web-logo">
                <label>{{ $t(`${i18nPrefix}.label.onWeb`) }}</label>
                <div class="web-logo-frame" @click="handlePreviewImage(previewLogoImage)">
                  <img class="web-img" :src="previewLogoImage || $DjangoStaticUrl('images_en/default_company.jpg')" />
                </div>
              </div>
              <div class="app-logo">
                <label>{{ $t(`${i18nPrefix}.label.onApp`) }}</label>
                <div class="app-logo-frame" @click="handlePreviewImage(previewLogoImage)">
                  <img class="app-img" :src="previewLogoImage || $DjangoStaticUrl('images_en/default_company.jpg')" />
                </div>
              </div>
            </div>
          </div>
        </template>
        <template #licensePhoto>
          <div class="upload-setting">
            <div class="image-selector">
              <el-button class="select-file" @click="handleSelectLicenseImage">{{ $t(`${i18nPrefix}.btn.upload`) }}</el-button>
              <input ref="licenseFileSelector" class="file-selector" type="file" :accept="acceptImageFileTypeList.join(',')" @change="handleLicenseImageChange"/>
              <div class="license-tip">{{ $t(`${i18nPrefix}.label.licenseTip`) }}</div>
            </div>
            <div class="license-container">
              <div class="license-frame" @click="handlePreviewImage(previewLicenseImage)">
                <img :src="previewLicenseImage" />
              </div>
            </div>
          </div>
        </template>
        <template #cnDescription="{ lineName }">
          <TinymceEditor v-if="!hiddenTinymce" v-model="companyInfo[lineName]" class="company-description"></TinymceEditor>
        </template>
        <template #description="{ lineName }">
          <TinymceEditor v-if="!hiddenTinymce" v-model="companyInfo[lineName]" class="company-description"></TinymceEditor>
        </template>
        <template #emailSuffix="{ lineName }">
          <el-select
            v-model="companyInfo[lineName]"
            multiple
            clearable
            allowCreate
            filterable
            defaultFirstOption
            :placeholder="$t(`${i18nPrefix}.placeholder.emailSuffixPlaceholder`)">
          </el-select>
          <div class="email-suffix-tip">{{ $t(`${i18nPrefix}.label.emailSuffixExample`) }}</div>
        </template>
        <template #candidateStatus>
          <div class="candidate-status-wrapper">
            <el-button class="candidate-status-btn" @click="loadCandidateStatusList">{{ $t(`${i18nPrefix}.btn.assign`) }}</el-button>
          </div>
        </template>
      </WrappedFrom>
      <div class="bottom-btn-group">
        <el-button class="cancel-btn" @click="handleCancel">{{ $t('common.btn.cancel') }}</el-button>
        <el-button class="save-btn" type="primary" @click="handleSave">{{ $t('common.btn.save') }}</el-button>
      </div>
      <div v-show="isPreviewImage" class="img-preview-mask">
        <div class="preview-wrapper">
          <img class="img-preview" :src="previewImage"/>
          <i class="close-preview-icon iconfont iconzengjiayulan-04" @click="handleClosePreviewImage"></i>
        </div>
      </div>
      <DialogWrapper
        class="candidate-status-assign-dialog"
        saveBtnLabelI18nKey="common.btn.confirm"
        :closeOnClickModal="false"
        :loading="candidateStatusAssignDialog.loading"
        :show.sync="candidateStatusAssignDialog.show"
        @save="handleSaveCandidateStatus"
      >
        <div class="candidate-status-list">
          <NoBorderTable
            v-rect-loading="loading"
            ref="elTable"
            noOperation
            tableHeight="400px"
            row-key="key"
            :showCreateBtn="false"
            :list-data="filteredCandidateStatusList"
            :table-columns="tableColumns"
            :rowClassName="getRowClassName"
            @select="handleSelect"
            @select-all="handleSelectAll"
          >
            <template #filter-group>
              <el-input v-model="keywords" clearable suffix-icon="el-icon-search" class="candidate-status-keywords" :placeholder="$t(`${i18nPrefix}.placeholder.keywords`)"></el-input>
            </template>
          </NoBorderTable>
        </div>
      </DialogWrapper>
      <DialogWrapper
        class="candidate-status-assign-dialog"
        cancelBtnLabelI18nKey="common.btn.back"
        :saveBtnLabel="$t(`${i18nPrefix}.btn.continue`)"
        :closeOnClickModal="false"
        :show.sync="isSaveProfileDialogShow"
        :title="$t(`${i18nPrefix}.label.validOrganization`)"
        @save="handleSaveProfile"
      >
      </DialogWrapper>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import { isEn, addI18nProperty } from '@/i18n'
import { mapActions } from 'vuex'
import WrappedFrom from '@/components/formEditor/wrappedForm.vue'
import TinymceEditor from './tinymceEditor.vue'
import { addQueryInPath, imageToBase64 } from '@/utils/common'
import DialogWrapper from '@/components/dialog/dialogWrapper.vue'
import NoBorderTable from '@/components/tableList/noBorderTable.vue'

export default {
  name: 'profile',
  props: {
    companyProfile: {
      type: Object,
      required: true
    },
    isProfileEdit: {
      type: Boolean,
      default: false
    }
  },
  components: {
    WrappedFrom,
    TinymceEditor,
    DialogWrapper,
    NoBorderTable
  },
  data () {
    return {
      i18nPrefix: 'admin.company.companyApprovalManagement',
      isEn,
      loading: false,
      hiddenTinymce: false,
      typeOptions: [],
      industryOptions: [],
      companySizeChoicesOptions: [],
      yearFoundedChoicesOptions: [],
      acceptImageFileTypeList: ['image/png', 'image/jpeg'],
      companyInfo: {
        cn_name: null,
        name: null,
        cn_legal_name: null,
        legal_name: null,
        contact_name: null,
        contact_mobile: null,
        type_id: null,
        primary_industry_id: null,
        year_founded: null,
        size: null,
        world_country_id: null,
        branch_num: null,
        world_province_id: null,
        world_city_id: null,
        license_number: null,
        address: null,
        website: null,
        attractive_words: null,
        cn_description: null,
        description: null,
        review_status: null,
        email_suffix: []
      },
      logoInfo: {
        image_base64: null,
        image: null,
        image_file_name: null
      },
      licenseInfo: {
        image_base64: null,
        image: null,
        image_file_name: null
      },
      keywords: null,
      candidateStatusList: [],
      candidateStatusSelection: [],
      candidateStatusDefaultSelection: [],
      candidateStatusAssignDialog: {
        show: false,
        loading: false,
        title: null,
        content: null
      },
      isSaveProfileDialogShow: false,
      canAssignStatus: true,
      isPreviewImage: false,
      previewImage: null
    }
  },
  computed: {
    companyId () {
      return this.$route.params.companyId
    },
    formLines () {
      const requiredMessage = this.$t('common.message.requiredFormField')
      const lines = {
        cn_name: {
          label: this.$t(`${this.i18nPrefix}.label.companyChineseDisplayName`),
          rules: [{ required: this.isProfileEdit, message: requiredMessage, trigger: 'blur' }]
        },
        name: {
          label: this.$t(`${this.i18nPrefix}.label.companyEnglishDisplayName`),
          rules: [{ required: this.isProfileEdit, message: requiredMessage, trigger: 'blur' }]
        },
        cn_legal_name: {
          label: this.$t(`${this.i18nPrefix}.label.companyChineseRegisteredName`)
        },
        legal_name: {
          label: this.$t(`${this.i18nPrefix}.label.companyEnglishRegisteredName`)
        },
        contact_name: {
          label: this.$t(`${this.i18nPrefix}.label.contactPersonName`),
          rules: [{ required: this.isProfileEdit, message: requiredMessage, trigger: 'blur' }]
        },
        contact_mobile: {
          label: this.$t(`${this.i18nPrefix}.label.contactPersonMobileNumber`),
          rules: [{ required: this.isProfileEdit, message: requiredMessage, trigger: 'blur' }]
        },
        type_id: {
          elType: 'el-select',
          label: this.$t(`${this.i18nPrefix}.label.companyType`),
          option: {
            data: this.typeOptions,
            valueKey: 'id',
            labelKey: 'i18nName'
          },
          props: { filterable: true },
          rules: [{ required: this.isProfileEdit, message: requiredMessage, trigger: 'blur' }]
        },
        primary_industry_id: {
          elType: 'el-select',
          label: this.$t(`${this.i18nPrefix}.label.industry`),
          option: {
            data: this.industryOptions,
            valueKey: 'id',
            labelKey: 'i18nName'
          },
          props: { filterable: true },
          rules: [{ required: this.isProfileEdit, message: requiredMessage, trigger: 'blur' }]
        },
        year_founded: {
          elType: 'el-select',
          label: this.$t(`${this.i18nPrefix}.label.yearFoundedInGreaterChina`),
          option: {
            data: this.yearFoundedChoicesOptions
          },
          props: { filterable: true }
        },
        size: {
          elType: 'el-select',
          label: this.$t(`${this.i18nPrefix}.label.employeesInGreaterChina`),
          option: {
            data: this.companySizeChoicesOptions
          },
          props: { filterable: true },
          rules: [{ required: this.isProfileEdit, message: requiredMessage, trigger: 'blur' }]
        },
        location: {
          label: this.$t(`${this.i18nPrefix}.label.location`),
          class: 'location-form-line',
          multiple: {
            world_country_id: {
              elType: 'el-select',
              class: 'county-select',
              placeholder: this.$t(`${this.i18nPrefix}.label.countryOrRegion`),
              option: {
                delayLoadData: this.loadCountryList
              },
              props: { filterable: true, clearable: true },
              clearOnChange: ['world_province_id', 'world_city_id']
            },
            world_province_id: {
              elType: 'el-select',
              class: 'province-select',
              placeholder: this.$t(`${this.i18nPrefix}.label.provinceOrState`),
              option: {
                reloadIfUnmatched: true,
                delayLoadData: this.loadProvinceList
              },
              props: { filterable: true, clearable: true },
              clearOnChange: ['world_city_id']
            },
            world_city_id: {
              elType: 'el-select',
              class: 'city-select',
              placeholder: this.$t(`${this.i18nPrefix}.label.cityOrTown`),
              option: {
                reloadIfUnmatched: true,
                delayLoadData: this.loadCityList
              },
              props: { filterable: true, clearable: true }
            }
          }
        },
        branch_num: {
          label: this.$t(`${this.i18nPrefix}.label.numberOfLocationsOrBranchesInChina`)
        },
        logo: {
          contentSlot: 'logo',
          label: this.$t(`${this.i18nPrefix}.label.logo`)
        },
        license_number: {
          label: this.$t(`${this.i18nPrefix}.label.licenseNumber`)
        },
        license_photo: {
          contentSlot: 'licensePhoto',
          label: this.$t(`${this.i18nPrefix}.label.businessLicense`)
        },
        address: {
          label: this.$t(`${this.i18nPrefix}.label.address`)
        },
        website: {
          label: this.$t(`${this.i18nPrefix}.label.website`)
        },
        attractive_words: {
          label: this.$t(`${this.i18nPrefix}.label.attractiveWords`),
          props: { maxlength: 20, showWordLimit: true }
        },
        cn_description: {
          contentSlot: 'cnDescription',
          label: this.$t(`${this.i18nPrefix}.label.companyChineseDescription`)
        },
        description: {
          contentSlot: 'description',
          label: this.$t(`${this.i18nPrefix}.label.companyEnglishDescription`)
        },
        review_status: {
          elType: 'el-radio-group',
          label: this.$t(`${this.i18nPrefix}.label.public`),
          option: {
            data: [
              { label: this.$t(`${this.i18nPrefix}.label.yes`), value: '1' },
              { label: this.$t(`${this.i18nPrefix}.label.no`), value: '0' }
            ]
          },
          class: 'review-status-radio'
        },
        email_suffix: {
          contentSlot: 'emailSuffix',
          label: this.$t(`${this.i18nPrefix}.label.emailSuffix`),
          rules: [
            {
              required: false,
              trigger: 'change',
              validator: (rule, value, callback) => {
                if (!value) {
                  callback()
                  return
                }
                const emailSuffix = this.validCompanyEmailSuffix(value)
                if (!emailSuffix) {
                  callback(new Error(this.$t(`${this.i18nPrefix}.message.emailSuffixError`)))
                  return
                }
                callback()
              }
            }
          ]
        },
        candidateStatus: {
          contentSlot: 'candidateStatus',
          label: this.$t(`${this.i18nPrefix}.label.candidateStatus`),
          hidden: !this.canAssignStatus
        }
      }

      return lines
    },
    previewLogoImage () {
      if (this.logoInfo.image_base64) {
        return this.logoInfo.image_base64
      }
      return this.$DjangoBaseUrl(addQueryInPath(this.logoInfo.image, { file_time_stamp: new Date().valueOf() }))
    },
    previewLicenseImage () {
      if (this.licenseInfo.image_base64) {
        return this.licenseInfo.image_base64
      }
      return this.$DjangoBaseUrl(addQueryInPath(this.licenseInfo.image, { file_time_stamp: new Date().valueOf() }))
    },
    tableColumns () {
      const columns = [
        {
          columnType: 'selection'
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.candidateStatusCnName`),
          dataKey: 'name_cn',
          showOverflowTooltip: true
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.candidateStatusEnName`),
          dataKey: 'name_en',
          showOverflowTooltip: true
        }
      ]
      return columns
    },
    filteredCandidateStatusList () {
      const keyword = _.trim(_.toLower(this.keywords))
      let list = this.candidateStatusList
      if (keyword) {
        list = _.filter(this.candidateStatusList, item => {
          const cnMatch = _.includes(_.toLower(item.name_cn), keyword)
          const enMatch = _.includes(_.toLower(item.name_en), keyword)
          return cnMatch || enMatch
        })
      }
      this.$nextTick(() => _.forEach(this.candidateStatusSelection, item => {
        const matchItem = _.find(list, i => i.key === item)
        if (matchItem) {
          this.$refs.elTable.elTable.toggleRowSelection(matchItem, true)
        }
      }))

      return list
    }
  },
  methods: {
    ...mapActions('candidateStatus', ['getCompanyBingingCandidateStatusList', 'updateCompanyBingingCandidateStatus']),
    ...mapActions('worldLocation', ['getCountryList', 'getProvinceList', 'getCityList']),

    loadCompanyInfo () {
      if (!this.companyProfile) {
        return
      }

      this.companyInfo = this.companyProfile.company_info || {}
      this.companyInfo.email_suffix = this.companyInfo.email_suffix ? _.split(this.companyInfo.email_suffix, '|') : []

      this.typeOptions = _.map(this.companyProfile.type_options, item => addI18nProperty(item, 'i18nName', 'cn_name', 'name'))
      this.industryOptions = _.map(this.companyProfile.industry_options, item => addI18nProperty(item, 'i18nName', 'cn_name', 'name'))
      this.companySizeChoicesOptions = _.map(this.companyProfile.company_size_choices, item => ({ value: item[0], label: item[1] }))
      this.yearFoundedChoicesOptions = _.map(this.companyProfile.year_founded_choices, item => ({ value: item[0], label: item[1] }))
      this.logoInfo.image = this.companyProfile.company_info?.logo
      this.licenseInfo.image = this.companyProfile.company_info?.license_photo
      this.canEditCandidatePrivacy = this.companyProfile.can_edit_candidate_privacy_setting
      this.canAssignStatus = this.companyProfile.can_assign_status_setting && this.hasAnyPermission(['PERM_ADMIN_STATUS_MANAGE'])
    },
    formatSimpleCompanyInfo (infoItem) {
      const info = {
        ...infoItem,
        value: infoItem.id || infoItem.value
      }
      addI18nProperty(info, 'label', 'cn_name', 'name')
      return info
    },
    async loadCountryList () {
      const resp = await this.getCountryList()
      if (resp) {
        this.countryList = _.map(resp.location_list, this.formatSimpleCompanyInfo)
      }
      return this.countryList
    },
    async loadProvinceList () {
      if (this.companyInfo.world_country_id && !isNaN(this.companyInfo.world_country_id)) {
        const params = {
          parentId: this.companyInfo.world_country_id
        }
        const resp = await this.getProvinceList(params)
        if (resp) {
          this.provinceList = _.map(resp.location_list, this.formatSimpleCompanyInfo)
        }
        return this.provinceList
      } else {
        return []
      }
    },
    async loadCityList () {
      if (this.companyInfo.world_province_id && !isNaN(this.companyInfo.world_province_id)) {
        const params = {
          parentId: this.companyInfo.world_province_id
        }
        const resp = await this.getCityList(params)
        if (resp) {
          this.cityList = _.map(resp.location_list, this.formatSimpleCompanyInfo)
        }
        return this.cityList
      } else {
        return []
      }
    },
    validateImage (file) {
      if (!_.includes(this.acceptImageFileTypeList, file.type)) {
        this.$message.error({
          showClose: true,
          message: this.$t(`${this.i18nPrefix}.message.wrongPhotoFileType`, { imageType: 'jpg, png' })
        })
        return false
      }
      return true
    },
    validCompanyEmailSuffix (emailSuffix) {
      // 去除每个元素的空白字符并过滤空值
      const suffixArray = _.chain(emailSuffix).map(_.trim).filter(_.negate(_.isEmpty)).value()
      this.companyInfo.email_suffix = suffixArray
      const pattern = /^[A-Za-z0-9-.]+$/
      const hasInvalid = _.some(suffixArray, suffix => {
        if (!pattern.test(suffix)) {
          return true
        }
        return false
      })
      return !hasInvalid
    },
    handleSelectLogoImage () {
      this.$refs.logoFileSelector.click()
    },
    async handleLogoImageChange () {
      const file = this.$refs.logoFileSelector.files[0]
      if (!file) {
        return
      }
      if (!this.validateImage(file)) {
        return
      }
      const imgBase64 = await imageToBase64(window.URL.createObjectURL(file))
      this.logoInfo.image_base64 = imgBase64
      this.logoInfo.image = imgBase64
      this.logoInfo.image_file_name = file.name
    },
    handleSelectLicenseImage () {
      this.$refs.licenseFileSelector.click()
    },
    async handleLicenseImageChange () {
      const file = this.$refs.licenseFileSelector.files[0]
      if (!file) {
        return
      }
      if (!this.validateImage(file)) {
        return
      }
      const imgBase64 = await imageToBase64(window.URL.createObjectURL(file))
      this.licenseInfo.image_base64 = imgBase64
      this.licenseInfo.image = imgBase64
      this.licenseInfo.image_file_name = file.name
    },
    handlePreviewImage (imageUrl) {
      this.previewImage = imageUrl
      this.isPreviewImage = true
    },
    handleClosePreviewImage () {
      this.isPreviewImage = false
      this.previewImage = null
    },
    async loadCandidateStatusList () {
      this.candidateStatusList = []
      this.candidateStatusSelection = []
      this.candidateStatusDefaultSelection = []
      this.candidateStatusAssignDialog.show = true
      this.candidateStatusAssignDialog.loading = true
      try {
        const resp = await this.getCompanyBingingCandidateStatusList(this.companyInfo.id)
        if (resp) {
          this.candidateStatusList = resp
          this.$nextTick(() =>
            _.forEach(this.candidateStatusList, item => {
              if (item.is_default || item.is_assigned) {
                this.$refs.elTable.elTable.toggleRowSelection(item, true)
                this.candidateStatusSelection.push(item.key)
                if (item.is_default) {
                  this.candidateStatusDefaultSelection.push(item)
                }
              }
            }))
        }
      } finally {
        this.candidateStatusAssignDialog.loading = false
      }
    },
    handleSelect (selection, row) {
      const select = _.find(selection, item => item.key === row.key)
      if (select) {
        if (!this.candidateStatusSelection.includes(row.key)) {
          this.candidateStatusSelection.push(row.key)
        }
      } else {
        const index = this.candidateStatusSelection.indexOf(row.key)
        if (index !== -1) {
          this.candidateStatusSelection.splice(index, 1)
        }
      }
    },
    handleSelectAll (selection) {
      if (_.isEmpty(selection)) {
        this.$nextTick(() => _.forEach(this.candidateStatusDefaultSelection, item => this.$refs.elTable.elTable.toggleRowSelection(item, true)))
        this.candidateStatusSelection = _.map(this.candidateStatusDefaultSelection, item => item.key)
      } else {
        this.candidateStatusSelection = _.map(this.candidateStatusList, item => item.key)
      }
    },
    getRowClassName ({ row }) {
      if (row.is_default) {
        return 'disabled-row'
      }
      return ''
    },
    async handleSaveCandidateStatus () {
      this.candidateStatusAssignDialog.loading = true
      const params = {
        data: {
          keys: this.candidateStatusSelection
        },
        companyId: this.companyInfo.id
      }
      try {
        await this.updateCompanyBingingCandidateStatus(params)
        this.candidateStatusAssignDialog.show = false
        this.candidateStatusList = []
        this.candidateStatusSelection = []
        this.candidateStatusDefaultSelection = []
      } finally {
        this.candidateStatusAssignDialog.loading = false
      }
    },
    handleCancel () {
      this.$emit('cancel')
    },
    async handleSave () {
      const result = await this.$refs.form.submit()
      if (!result) {
        return
      }

      if (this.isProfileEdit && (!this.companyInfo.license_number || !this.licenseInfo.image || !this.companyInfo.address)) {
        this.isSaveProfileDialogShow = true
        return
      }
      this.handleSaveProfile()
    },
    handleSaveProfile () {
      this.isSaveProfileDialogShow = false
      const companyInfoCopy = _.cloneDeep(this.companyInfo)
      companyInfoCopy.email_suffix = companyInfoCopy.email_suffix ? _.join(companyInfoCopy.email_suffix, '|') : ''
      const params = {
        data: {
          company_info: companyInfoCopy,
          logo_info: this.logoInfo,
          license_info: this.licenseInfo
        },
        companyId: this.companyInfo.id
      }
      this.$emit('save', params)
    }
  },
  watch: {
    '$i18n.locale': {
      handler () {
        this.hiddenTinymce = true
        this.$nextTick(() => {
          this.hiddenTinymce = false
        })
      }
    },
    companyProfile: {
      handler () {
        this.loadCompanyInfo()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.edit-company-container {
  .company-info-container {
    width: 800px;
    margin: 30px auto 0;

    &.wide-mode {
      width: 950px;
    }

    .upload-setting {
      display: flex;
      justify-content: space-between;
      margin-top: -1px;
      .image-selector {
        display: inline-block;
        vertical-align: middle;

        .logo-tip {
          margin-top: 5px;
          line-height: 20px;
        }

        .license-tip {
          margin-top: 5px;
          line-height: 20px;
        }

        .select-file {
          font-size: 12px;
          border: 0;
          cursor: pointer;
          line-height: 12px;
          padding: 8px 24px;
          background: #0096cc;
          color: #fff;
          font-weight: 500;
          ::v-deep {
            span {
              display: inline-block;
              margin-top: 1px;
            }
          }
        }
        .file-selector {
          display: none;
        }
      }
      .logo-container {
        display: flex;
        margin-left: 20px;
        margin-top: 1px;
        .web-logo {
          width: 155px;
          .web-logo-frame {
            display: flex;
            height: 70px;
            border: 1px solid #ededed;
            justify-content: center;
            align-items: center;
            cursor: pointer;
            .web-img {
              max-width: 132px;
              max-height: 60px;
            }
          }
        }

        .app-logo {
          display: flex;
          justify-content: center;
          align-items: center;
          flex-direction: column;
          margin-left: 24px;
          .app-logo-frame {
            display: flex;
            margin-top: 5px;
            height: 60px;
            width: 60px;
            justify-content: center;
            align-items: center;
            cursor: pointer;
            .app-img {
              object-fit: cover;
              display: block;
              height: 100%;
              width: 100%;
              border: 1px solid #ededed;
              border-radius: 50%;
            }
          }
          label {
            margin-top: -5px;
          }
        }
      }
      .license-container {
        display: flex;
        width: 240px;
        margin-left: 20px;
        .license-frame {
          display: flex;
          width: 240px;
          height: 160px;
          border: 1px solid #ededed;
          justify-content: center;
          align-items: center;
          cursor: pointer;
          img {
            max-width: 100%;
            max-height: 100%;
          }
        }
      }
    }

    .company-description {
      flex: 1;
    }

    .email-prefix-tip {
      margin-top: 5px;
      line-height: 20px;
    }

    .email-suffix-tip {
      line-height: 20px;
    }

    ::v-deep {
      .multiple-line.location-form-line {
        .el-form-item__content {
          display: flex;
          align-content: flex-start;
          flex-flow: row wrap;
        }
        .multiple-line-item.county-select {
          margin-right: 12px;
          flex: 1;
        }
        .multiple-line-item.province-select {
          margin-right: 12px;
          flex: 1;
        }
        .multiple-line-item.city-select {
          flex: 1;
        }
      }

      .review-status-radio {
        .el-form-item__content {
          .el-radio-group {
            margin-bottom: 3px;
          }
        }
      }
    }
    .candidate-status-wrapper {
      margin-top: -1px;
      .candidate-status-btn {
        line-height: 12px;
        border: 0;
        cursor: pointer;
        font-size: 12px;
        font-weight: 500;
        padding: 8px 14px;
        background: #0096cc;
        border-color: #0096cc;
        color: #ffffff;
      }
    }

    .candidate-status-keywords {
      width: 230px;
    }

    ::v-deep .disabled-row .el-table__cell {
      pointer-events: none;
      background-color: #f5f7fa;
      border-color: #e4e7ed;
      color: #c0c4cc;
    }

    .bottom-btn-group {
      display: flex;
      justify-content: flex-end;
      margin: 30px auto 0;
      .el-button {
        padding: 8px 28px;
        border-radius: 2px;
        line-height: 22px;
        font-size: 16px;
        font-weight: 500;
        color: #0096cc;
        &:last-child {
          margin-left: 24px;
          margin-right: 0;
          background: #0096cc;
          border-color: #0096cc;
          color: #ffffff;
        }
      }
    }

    .img-preview-mask {
      position: fixed;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      overflow: auto;
      margin: 0;
      z-index: 2;
      background: rgba(0, 0, 0, .4);

      .preview-wrapper {
        position: relative;

        .img-preview {
          display: block;
          position: absolute;
          top: 150px;
          left: 50%;
          transform: translateX(-50%);
          width: 500px;
          height: 500px;
          line-height: 1;
          object-fit: contain;
        }

        .close-preview-icon {
          position: absolute;
          top: 150px;
          left: 50%;
          transform: translateX(-50%);
          font-size: 40px;
          margin-left: 335px;
          cursor: pointer;
        }
      }
    }
  }
}
</style>
